import * as React from 'react';
import * as cx from 'classnames';

import SuperCritical from 'lib/supercritical';
import { tzmoment } from 'lib/tzmoment';

import { CoordinationCardStatuses } from 'data/models/CoordinationCardStatuses';
import { EventType } from 'data/models/EventType';

import {
    AvailableStatus,
    CoordinationCardDetailsStatusTab as StatusTab,
    PendingStatusUpdate
} from './status';

import { CCardStatusIndicator } from 'portcall/ccardstatusindicator/view';

export const CoordinationCardDetailsStatusTabView = SuperCritical(({ model: m } : { model : StatusTab }) =>
    <div className="coordinationcarddetailsstatustab">
        {m.PendingStatusUpdate() ?
            <PendingStatusUpdateView update={m.PendingStatusUpdate()!} /> :
            <MainStatusView model={m} />
        }
    </div>
);

const PendingStatusUpdateView = SuperCritical(({ update } : { update : PendingStatusUpdate }) =>
    <div>
        <div className="status-summary">
            <CCardStatusIndicator statusId={update.Request.StatusId()} />
            <span className="status-summary-text">
                <span className="status-label">{update.StatusLabel()}</span>
            </span>
        </div>
        <PendingStatusReason update={update} />
        <PendingStatusUpdateControls update={update} />
    </div>
);

const PendingStatusReason = SuperCritical(({ update } : { update : PendingStatusUpdate }) =>
    <div className="pending-status-reason-shell">
        <textarea
            className={cx('pending-status-reason', {
                required: update.IsStatusReasonRequired()
            })}
            value={update.Request.StatusReason() ?? ''}
            onChange={e => update.Request.StatusReason(e.currentTarget.value || null)}
            placeholder={`Comment (${update.IsStatusReasonRequired() ? 'required' : 'optional'})...`}
            autoFocus
            onFocus={e => e.currentTarget.select()}
        />
    </div>
);

const PendingStatusUpdateControls = SuperCritical(({ update } : { update : PendingStatusUpdate }) =>
    <div className="pending-status-update-ctrls">
        <button
            className="pc-button submit-btn"
            onClick={update.Submit}
            disabled={!update.CanSubmit()}
        >
            Update
        </button>
        <button
            className="pc-button cancel-btn"
            onClick={update.Cancel}
        >
            Cancel
        </button>
    </div>
);

const MainStatusView = SuperCritical(({ model: m } : { model : StatusTab }) =>
    <div>
        <div className="status-summary">
            <CCardStatusIndicator statusId={m.CoordinationCard.StatusId()} />
            <span className="status-summary-text">
                <span className="status-label">{m.CurrentStatusLabel()}</span>
                {' '}
                <StatusShortExplanation model={m} />
            </span>
        </div>
        <StatusDetailedExplanation model={m} />
        <StatusReason model={m} />
        <AvailableStatuses model={m} />
    </div>
);

const StatusShortExplanation = SuperCritical(({ model: m } : { model : StatusTab }) =>
    <span className="status-short-expl">{StatusShortExplanationText(m)}</span>
);

function StatusShortExplanationText(m : StatusTab) {
    if (m.CoordinationCard.StatusId() === CoordinationCardStatuses.Pending) {
        const provider = m.CoordinationCard.Provider()?.Provider();
        return provider ? `from ${provider.Name()}` : '';
    } else {
        const
            expl = m.StatusExplanatoryEvent(),
            org = expl?.User()?.Organization(),
            automation = expl?.Automation();
        return org ? `by ${org.Name()}` : automation ? `by ${automation.Name()}` : '';
    }
}

const StatusDetailedExplanation = SuperCritical(({ model: m } : { model : StatusTab }) =>
    <div className="status-detailed-expl">{StatusDetailedExplanationText(m)}</div>
);

function StatusDetailedExplanationText(m : StatusTab) {
    const expl = m.StatusExplanatoryEvent();
    if (expl) {
        const
            timeAgo = tzmoment(expl.Date()).from(m.StatusExplanationNow()),
            actorName = expl.ActorAbbreviatedName();
        if (expl.Type() === EventType.CCardStatusUpdate) {
            if (expl.CoordinationCardVersion()?.StatusId() === CoordinationCardStatuses.Pending) {
                return `milestone updated ${timeAgo}`;
            } else {
                return `updated ${timeAgo} by ${actorName}`;
            }
        } else if (expl.Type() === EventType.CCardRequested) {
            return `coordination requested ${timeAgo}`
        } else if (expl.Type() === EventType.CCardProviderChanged) {
            return `provider changed ${timeAgo} by ${actorName}`;
        } else {
            return '';
        }
    } else {
        return '';
    }
}

const StatusReason = SuperCritical(({ model: m } : { model : StatusTab }) =>
    m.CoordinationCard.StatusReason() ?
        <div className="status-reason">
            <span className="author">{StatusReasonAuthor(m)}</span>
            {' '}
            {m.CoordinationCard.StatusReason()}
        </div> :
        null
);

function StatusReasonAuthor(m : StatusTab) {
    const authorName = m.StatusReasonExplanatoryEvent()?.ActorAbbreviatedName();
    return authorName ? `${authorName}:` : '';
}

const AvailableStatuses = SuperCritical(({ model: m } : { model : StatusTab }) =>
    m.AvailableStatuses().length > 0 ?
        <div className="available-statuses">
            <div className="available-statuses-title">Select Status Update</div>
            {m.AvailableStatuses().map(s =>
                <AvailableStatusView status={s} key={s.Model.Id()} />
            )}
        </div> :
        null
);

const AvailableStatusView = SuperCritical(({ status: s } : { status : AvailableStatus }) =>
    <div className="available-status" onClick={s.Select}>
        <CCardStatusIndicator statusId={s.Model.Id()} />
        <span className="status-label">{s.Label()}</span>
    </div>
);



// WEBPACK FOOTER //
// ./apps/portcall/coordinationcarddetails/statusview.tsx